import { Box, BoxProps, Button, Flex, Input, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

import { RasterStyle } from "@nens/lizard/api-v4";
import { useClickOutside } from "@nens/react/hooks";

import { RasterLegend } from "../raster-legend/raster-legend";

function LegendValue({
  value,
  unit,
  onChange,
  textAlign = "left",
}: {
  value: number;
  unit?: string;
  onChange: (value: number) => void;
  textAlign?: "left" | "right";
}) {
  const [editable, setEditable] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => setEditable(false));
  useEffect(() => {
    const keyPressFunction = (event: KeyboardEvent) => {
      if (event.code === "Escape") setEditable(false);
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        setEditable(false);
      }
    };
    document.addEventListener("keydown", keyPressFunction);
    return () => document.removeEventListener("keydown", keyPressFunction);
  }, []);

  return (
    <Box ref={ref}>
      {editable ? (
        <Input
          w="100px"
          type="number"
          autoFocus
          defaultValue={value}
          textAlign={textAlign}
          size="sm"
          my="5px"
          h="26px"
          onChange={(e) => onChange(Number(e.target.value))}
        />
      ) : (
        <Button
          variant="unstyled"
          onClick={() => setEditable(true)}
          _hover={{ bgColor: "#f4f4f4" }}
          w="62px"
          my="5px"
          h="26px"
        >
          <Text fontSize="xs" textAlign={textAlign} mx="1">
            {value} {unit || ""}
          </Text>
        </Button>
      )}
    </Box>
  );
}

interface LegendProps extends BoxProps {
  layer: string;
  name: string;
  unit: string;
  rasterStyle: RasterStyle;
  setRasterStyle: (style: string | null) => void;
  setRasterMin: (min: number) => void;
  setRasterMax: (max: number) => void;
}

export function Legend({
  layer,
  name,
  unit,
  rasterStyle,
  setRasterStyle,
  setRasterMin,
  setRasterMax,
  ...rest
}: LegendProps) {
  const min = rasterStyle.getMin();
  const max = rasterStyle.getMax();

  return (
    <Flex
      flexDirection="column"
      px="2"
      py="1"
      bg="white"
      position="absolute"
      top="60px"
      left="10px"
      rounded="md"
      shadow="lg"
      cursor="pointer"
      {...rest}
    >
      <Flex>
        <Text
          fontSize="sm"
          my="4px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          fontWeight="normal"
        >
          {name || "-"}
        </Text>
      </Flex>
      <RasterLegend layer={layer} rasterStyle={rasterStyle} setRasterStyle={setRasterStyle} />
      <Flex flexDirection="row" justifyContent="space-between" fontSize="xs" alignItems="center">
        {min !== null ? (
          <LegendValue value={min} unit={unit} textAlign="left" onChange={setRasterMin} />
        ) : (
          <Box />
        )}
        {max !== null ? (
          <LegendValue value={max} unit={unit} textAlign="right" onChange={setRasterMax} />
        ) : (
          <Box />
        )}
      </Flex>
    </Flex>
  );
}
